/* import __COLOCATED_TEMPLATE__ from './color-pickers.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { tinycolor } from '@intercom/intercom-messenger-colors';
import { DEFAULT_MESSENGER_COLORS } from 'embercom/lib/messenger-settings-defaults';
import { debounce } from '@ember/runloop';

export default class BackgroundColorPickers extends Component {
  @tracked showGradientThirdColorLight;
  @tracked showGradientThirdColorDark;
  @service MessengerSettingsService;
  @service messengerSettingsThemeProvider;
  @service appService;

  colorOptions = DEFAULT_MESSENGER_COLORS;

  constructor(owner, args) {
    super(owner, args);

    if (!this.gradientFirstColor) {
      this.setGradientFirstColor(
        tinycolor(this.args.configuration.appColors.gradient_start_color).toHexString(),
      );
    }
    if (!this.gradientSecondColor) {
      this.setGradientSecondColor(
        tinycolor(this.args.configuration.appColors.gradient_end_color).toHexString(),
      );
    }
    this.showGradientThirdColorLight = !!this.args.activeBrand.gradientThirdColor;
    this.showGradientThirdColorDark = !!this.args.activeBrand.gradientThirdColorDark;
  }

  get activeBrand() {
    return this.args.activeBrand;
  }

  get selectedTheme() {
    return this.messengerSettingsThemeProvider.targetThemeType;
  }

  get showGradientThirdColor() {
    return this.selectedTheme === 'dark'
      ? this.showGradientThirdColorDark
      : this.showGradientThirdColorLight;
  }

  getThemeProperty(lightProperty, darkProperty) {
    return this.selectedTheme === 'dark' ? darkProperty : lightProperty;
  }

  get gradientFirstColorProperty() {
    return this.getThemeProperty('gradientFirstColor', 'gradientFirstColorDark');
  }

  get gradientSecondColorProperty() {
    return this.getThemeProperty('gradientSecondColor', 'gradientSecondColorDark');
  }

  get gradientThirdColorProperty() {
    return this.getThemeProperty('gradientThirdColor', 'gradientThirdColorDark');
  }

  get gradientFirstColor() {
    return this.activeBrand[this.gradientFirstColorProperty];
  }

  get gradientSecondColor() {
    return this.activeBrand[this.gradientSecondColorProperty];
  }

  get gradientThirdColor() {
    return this.activeBrand[this.gradientThirdColorProperty];
  }

  get gradientFirstColorHex() {
    return this.activeBrand[
      this.getThemeProperty('gradientFirstColorHex', 'gradientFirstColorDarkHex')
    ];
  }

  get gradientSecondColorHex() {
    return this.activeBrand[
      this.getThemeProperty('gradientSecondColorHex', 'gradientSecondColorDarkHex')
    ];
  }

  get gradientThirdColorHex() {
    return this.activeBrand[
      this.getThemeProperty('gradientThirdColorHex', 'gradientThirdColorDarkHex')
    ];
  }

  get showAddGradientColor() {
    return !this.showGradientThirdColor;
  }

  @action
  updateGradientColorsInputs() {
    this[this.getThemeProperty('showGradientThirdColorLight', 'showGradientThirdColorDark')] =
      !!this.activeBrand[this.gradientThirdColorProperty];
  }

  @action
  setGradientFirstColor(color) {
    this.activeBrand[this.gradientFirstColorProperty] = color;

    if (this.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[
        this.getThemeProperty('gradientFirstColor', 'gradientFirstColorDark')
      ] = color;
    }

    this.debouncedTrackColorChange(
      this.getThemeProperty('gradient_first_color', 'gradient_first_color_dark'),
      color,
    );
  }

  @action
  setGradientSecondColor(color) {
    this.activeBrand[this.gradientSecondColorProperty] = color;

    if (this.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[
        this.getThemeProperty('gradientSecondColor', 'gradientSecondColorDark')
      ] = color;
    }

    this.debouncedTrackColorChange(
      this.getThemeProperty('gradient_second_color', 'gradient_second_color_dark'),
      color,
    );
  }

  @action
  setGradientThirdColor(color) {
    this.activeBrand[this.gradientThirdColorProperty] = color;

    if (this.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[
        this.getThemeProperty('gradientThirdColor', 'gradientThirdColorDark')
      ] = color;
    }

    this.debouncedTrackColorChange(
      this.getThemeProperty('gradient_third_color', 'gradient_third_color_dark'),
      color,
    );
  }

  debouncedTrackColorChange(object, color) {
    debounce(
      this.MessengerSettingsService,
      this.MessengerSettingsService.trackAnalyticsEvent,
      {
        action: 'changed',
        object,
        color,
      },
      500,
    );
  }

  @action
  addGradientColor() {
    if (!this.showGradientThirdColor) {
      let gradientThirdColor = tinycolor(this.gradientSecondColor);
      gradientThirdColor.spin(90);
      this.setGradientThirdColor(gradientThirdColor.toHexString());
      this[this.getThemeProperty('showGradientThirdColorLight', 'showGradientThirdColorDark')] =
        true;
      return;
    }
  }

  @action
  removeThirdGradient() {
    this.activeBrand[this.gradientThirdColorProperty] = '';

    if (this.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[
        this.getThemeProperty('gradientThirdColor', 'gradientThirdColorDark')
      ] = '';
    }
    this[this.getThemeProperty('showGradientThirdColorLight', 'showGradientThirdColorDark')] =
      false;
  }
}
